import { useRef, useState } from 'react';
import ClinicalPatientSearch, { FormClientHeaderContext } from '../clinical/ClinicalPatientSearch';
import { getFormBridge } from '../../utils/openFormWindow';
import FormWorkspaceHeader from './FormWorkspaceHeader';

// New program forms render their real editor immediately. This header binds
// the editor to a client before any editing, autosave, or completion is enabled.
// Existing drafts have no clientSelection handoff and are left unchanged.
export default function FormClientBoundary({ children }) {
  const [context] = useState(() => getFormBridge()?.clientSelection);
  const [clientId, setClientId] = useState('');
  const [month, setMonth] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [selectorReset, setSelectorReset] = useState(0);
  const starting = useRef(false);
  if (!context) return children;
  const now = new Date();
  const maxMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const title = context.title || 'New document';
  const isHomeHealth = /supervision|care plan|pca|cfss|bill of rights|discharge|participant/i.test(title);
  const programLabel = context.programLabel || (isHomeHealth ? 'PCA / CFSS · Clinical document' : 'Adult Day Services · Clinical document');
  const description = context.description || (isHomeHealth
    ? 'Review participant information, service delivery, required follow-up, and authorization.'
    : 'Complete the required service documentation and authorization for this client.');
  const bind = async (id, reportingMonth) => {
    if (!id || starting.current || (context.monthly && (!/^\d{4}-\d{2}$/.test(reportingMonth) || reportingMonth > maxMonth))) return;
    starting.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      if (!getFormBridge()?.clientSelection || !context.patients.some(patient => patient.id === id)) throw new Error();
      await context.onSelect(id, reportingMonth);
    } catch (failure) {
      starting.current = false;
      setBusy(false);
      if (failure?.code === 'DRAFT_OPEN_CANCELLED') {
        setClientId('');
        // ClinicalPatientSearch retains its own selected-client state. Reset
        // only this inactive selector, not the form or other clinical editors.
        setSelectorReset(value => value + 1);
        setNotice('Existing draft left unchanged. Select a client when you’re ready to continue.');
        return;
      }
      setError(failure?.code === 'REVIEW_PARENT_REQUIRED'
        ? 'A completed or approved parent plan is required to start a new review. Complete the parent plan, then reopen this review form.'
        : 'Client information could not be loaded. Try again or reopen this form from the document list.');
    }
  };
  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gray-100 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      <FormWorkspaceHeader title={title} programLabel={programLabel} description={description} preview onClose={() => window.close()}>
          <section aria-label="Document client" className="mt-4 grid items-end gap-3 border-t border-slate-700 pt-3 sm:grid-cols-[minmax(0,1fr)_auto]">
          <ClinicalPatientSearch key={selectorReset} inverse label="Document client" patients={context.patients} programFilter={null} selectedPatientId={clientId} disabled={busy}
            onPatientSelect={patient => { setClientId(patient.id); bind(patient.id, month); }} />
          {context.monthly && <label className="block text-sm">Reporting month *<input type="month" value={month} max={maxMonth} disabled={busy} onChange={event => { setMonth(event.target.value); bind(clientId, event.target.value); }} className="ml-3 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2" /></label>}
          <p role="status" className="pb-2 text-xs text-slate-300">{busy ? 'Loading client information…' : error ? 'Client selected. Resolve the issue below to begin editing.' : notice || (context.monthly ? 'Select a client and reporting month to begin.' : 'Select a client to begin. The form preview is available below.')}</p>
          {!context.patients.length && <p role="status" className="text-xs text-amber-200 sm:col-span-2">No clients are available in this program. Check enrollment before creating a document.</p>}
          {error && <div role="alert" className="text-sm text-red-200 dark:text-red-200">{error} <button type="button" className="underline min-h-11 px-2" onClick={() => bind(clientId, month)}>Retry</button></div>}
          </section>
      </FormWorkspaceHeader>
      {/* Mount the actual form, not a second setup page. Read-only handoff also
          blocks autosave; inert prevents controls with custom event handlers. */}
      <FormClientHeaderContext.Provider value={true}>
        <div role="region" aria-label="Form preview" tabIndex={0} className="mx-auto w-full max-w-[1480px] flex-1 min-h-0 overflow-y-auto bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-600 dark:bg-gray-900">
          {/* The scroll surface must remain outside the inert editor. Expand
              nested window scroll panes so their content belongs to this surface. */}
          <div inert={true} aria-disabled="true" className="[&_.h-screen]:!h-auto [&_.h-full]:!h-auto [&_.min-h-screen]:!min-h-0 [&_.overflow-y-auto]:!overflow-visible [&_.overflow-auto]:!overflow-visible [&_.overflow-hidden]:!overflow-visible [&_input]:!opacity-100 [&_textarea]:!opacity-100 [&_select]:!opacity-100 [&_input]:!bg-white [&_textarea]:!bg-white [&_select]:!bg-white dark:[&_input]:!bg-gray-700 dark:[&_textarea]:!bg-gray-700 dark:[&_select]:!bg-gray-700 [&_input]:!text-gray-900 [&_textarea]:!text-gray-900 [&_select]:!text-gray-900 dark:[&_input]:!text-gray-100 dark:[&_textarea]:!text-gray-100 dark:[&_select]:!text-gray-100">{children}</div>
        </div>
      </FormClientHeaderContext.Provider>
    </div>
  );
}
